import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'
import { cardsApi } from '@/api/cardsApi'
import { useEnumsStore } from '@/stores/enumsStore'
import { useAuthStore } from '@/stores/authStore'
import type { CardWord } from '@/types'

function normalizeLine(line: string): string {
  let s = line.trim()
  if (s.endsWith(',')) s = s.slice(0, -1).trim()
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function parseLines(text: string): string[] {
  return text
    .split(/\n/)
    .map(normalizeLine)
    .filter(Boolean)
}

function getAttr(word: CardWord, key: string): string {
  const a = word.attributes?.find((x) => x.key === key)
  return a?.value ?? ''
}

export type BulkCreateCardsModalResult = 'created' | 'cancelled'

interface BulkCreateCardsModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (result: BulkCreateCardsModalResult) => void
  userId?: number
}

type Step = 'input' | 'table'

interface RowState {
  text: string
  found: boolean
  word?: CardWord
  translation: string
  transcription: string
  part_of_speech: string
  type: string
}

export function BulkCreateCardsModal({
  open,
  onClose,
  onSuccess,
  userId,
}: BulkCreateCardsModalProps) {
  const { t } = useTranslation()
  const { enums, loadEnums } = useEnumsStore()
  const roles = useAuthStore((s) => s.user?.roles) ?? []
  const requiresTeacherAttributes = roles.some((r) => r.slug === 'teacher' || r.slug === 'admin')
  useEffect(() => {
    if (open) loadEnums().catch(() => {})
  }, [open, loadEnums])
  const [textareaValue, setTextareaValue] = useState('')
  const [step, setStep] = useState<Step>('input')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [subscriptionError, setSubscriptionError] = useState(false)
  const [successResult, setSuccessResult] = useState<{ created: number; skipped: number } | null>(null)
  const [rows, setRows] = useState<RowState[]>([])

  const reset = () => {
    setTextareaValue('')
    setStep('input')
    setError(null)
    setSubscriptionError(false)
    setSuccessResult(null)
    setRows([])
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleCheck = async () => {
    const texts = parseLines(textareaValue)
    if (texts.length === 0) {
      setError(t('cards.validation.text_required'))
      return
    }
    setError(null)
    setLoading(true)
    try {
      const payload: { texts: string[]; user_id?: number } = { texts }
      if (userId) payload.user_id = userId
      const result = await cardsApi.checkWords(payload)

      if (result.not_found.length === 0) {
        // Все слова в словаре — сразу создаём карточки (уже имеющиеся будут пропущены API)
        const payloadCreate: { words: Array<{ text: string }>; user_id?: number } = {
          words: result.found.map((w) => ({ text: w.text })),
        }
        if (userId) payloadCreate.user_id = userId
        const data = await cardsApi.bulkCreateCards(payloadCreate)
        const total = texts.length
        if (data.created === 0 && data.skipped === total) {
          setError(t('cards.errors.words_already_in_cards'))
        } else if (data.created === total && data.skipped === 0) {
          onSuccess('created')
          handleClose()
        } else {
          setSuccessResult({ created: data.created, skipped: data.skipped })
        }
      } else {
        const rowMap = new Map<string, RowState>()
        result.found.forEach((w) => {
          rowMap.set(w.text, {
            text: w.text,
            found: true,
            word: w,
            translation: getAttr(w, 'translation'),
            transcription: getAttr(w, 'transcription'),
            part_of_speech: getAttr(w, 'part_of_speech'),
            type: getAttr(w, 'type'),
          })
        })
        result.not_found.forEach((text) => {
          rowMap.set(text, {
            text,
            found: false,
            translation: '',
            transcription: '',
            part_of_speech: '',
            type: '',
          })
        })
        const ordered = texts.map((txt) => rowMap.get(txt)!).filter(Boolean)
        setRows(ordered)
        setStep('table')
      }
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { message?: string; error?: string } } }
      const isSubErr = e.response?.status === 403 && (e.response?.data?.error === 'subscription_parameter_denied' || e.response?.data?.error === 'subscription_limit_exceeded')
      const errorMessage = e.response?.data?.message || getErrorMessage(err)
      if (isSubErr) {
        setError(errorMessage || t('cards.errors.bulk_creation_disabled'))
        setSubscriptionError(true)
      } else {
        setError(errorMessage || t('cards.errors.load_failed'))
        setSubscriptionError(false)
      }
    } finally {
      setLoading(false)
    }
  }

  const updateRow = (
    index: number,
    field: 'translation' | 'transcription' | 'part_of_speech' | 'type',
    value: string
  ) => {
    setRows((prev) => {
      const next = [...prev]
      if (!next[index].found) {
        next[index] = { ...next[index], [field]: value }
      }
      return next
    })
  }

  const handleCreateFromTable = async () => {
    setError(null)
    if (requiresTeacherAttributes) {
      const invalid = rows.some(
        (row) =>
          !row.found &&
          (!row.transcription.trim() || !row.translation.trim()),
      )
      if (invalid) {
        setError(t('cards.validation.transcription_and_translation_required'))
        return
      }
    }
    setLoading(true)
    try {
      const words = rows.map((r) => {
        const item: { text: string; attributes?: Array<{ key: string; value: string }> } = {
          text: r.text,
        }
        if (!r.found && (r.translation || r.transcription || r.part_of_speech || r.type)) {
          item.attributes = []
          if (r.translation) item.attributes.push({ key: 'translation', value: r.translation })
          if (r.transcription) item.attributes.push({ key: 'transcription', value: r.transcription })
          if (r.part_of_speech) item.attributes.push({ key: 'part_of_speech', value: r.part_of_speech })
          if (r.type) item.attributes.push({ key: 'type', value: r.type })
        }
        return item
      })
      const payload: { words: typeof words; user_id?: number } = { words }
      if (userId) payload.user_id = userId
      await cardsApi.bulkCreateCards(payload)
      onSuccess('created')
      handleClose()
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { error?: string; message?: string } } }
      const isSubErr = e.response?.status === 403 && (e.response?.data?.error === 'subscription_limit_exceeded' || e.response?.data?.error === 'subscription_parameter_denied')
      const errorMessage = e.response?.data?.message || getErrorMessage(err)
      if (isSubErr) {
        setError(errorMessage || t('subscription.limit_exceeded'))
        setSubscriptionError(true)
      } else {
        setError(errorMessage || t('cards.errors.create_failed'))
        setSubscriptionError(false)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('cards.create_bulk')}</DialogTitle>
      <DialogContent>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => { setError(null); setSubscriptionError(false) }}>
              {error}
              {subscriptionError && (
                <Box sx={{ mt: 1 }}>
                  <Button component={Link} to={ROUTES.PROFILE_SUBSCRIPTION} variant="outlined" size="small">
                    {t('subscription.goToSubscription')}
                  </Button>
                </Box>
              )}
            </Alert>
          </Box>
        )}
        {successResult && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => { setSuccessResult(null); onSuccess('created'); handleClose() }}>
            {t('cards.messages.bulk_created_result', {
              created: successResult.created,
              skipped: successResult.skipped,
            })}
          </Alert>
        )}

        {step === 'input' && !successResult && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('cards.bulk_input_hint')}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={5}
              label={t('cards.word_column')}
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
              placeholder="hello&#10;world&#10;test"
            />
          </Box>
        )}

        {step === 'table' && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('cards.word_column')}</TableCell>
                  <TableCell>{t('cards.translation_column')}</TableCell>
                  <TableCell>{t('cards.transcription_column')}</TableCell>
                  <TableCell>{t('cards.part_of_speech')}</TableCell>
                  <TableCell>{t('cards.word_type')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={`${row.text}-${index}`}>
                    <TableCell>{row.text}</TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.translation}
                        onChange={(e) => updateRow(index, 'translation', e.target.value)}
                        disabled={row.found}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.transcription}
                        onChange={(e) => updateRow(index, 'transcription', e.target.value)}
                        disabled={row.found}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={row.part_of_speech}
                          displayEmpty
                          onChange={(e) => updateRow(index, 'part_of_speech', e.target.value)}
                          disabled={row.found}
                          renderValue={(v) =>
                            v === '' ? t('cards.filter.all') : enums?.PartOfSpeech?.find((o) => o.value === v)?.label ?? v
                          }
                        >
                          <MenuItem value="">{t('cards.filter.all')}</MenuItem>
                          {(enums?.PartOfSpeech ?? []).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={row.type}
                          displayEmpty
                          onChange={(e) => updateRow(index, 'type', e.target.value)}
                          disabled={row.found}
                          renderValue={(v) => {
                            if (v === '') return t('cards.filter.all')
                            if (v === 'word') return t('cards.filter_type_word')
                            if (v === 'phrase') return t('cards.filter_type_phrase')
                            return enums?.WordType?.find((o) => o.value === v)?.label ?? v
                          }}
                        >
                          <MenuItem value="">{t('cards.filter.all')}</MenuItem>
                          {(enums?.WordType ?? []).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {step === 'input' && (
          <>
            {successResult ? (
              <Button variant="contained" onClick={() => { setSuccessResult(null); onSuccess('created'); handleClose() }}>
                {t('common.ok')}
              </Button>
            ) : (
              <>
                <Button onClick={handleClose}>{t('common.cancel')}</Button>
                <Button
                  variant="contained"
                  onClick={handleCheck}
                  disabled={loading || parseLines(textareaValue).length === 0}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  {t('cards.add_words')}
                </Button>
              </>
            )}
          </>
        )}
        {step === 'table' && (
          <>
            <Button onClick={() => setStep('input')}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              onClick={handleCreateFromTable}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {t('cards.create_words_and_add_cards')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
